import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "ui-search-dropdown__required",
  "aria-hidden": "true"
}
const _hoisted_2 = { class: "ui-search-dropdown__control" }
const _hoisted_3 = ["value", "placeholder", "disabled", "required", "aria-label", "aria-expanded", "onKeydown"]
const _hoisted_4 = {
  key: 0,
  class: "ui-search-dropdown__empty"
}
const _hoisted_5 = {
  key: 1,
  class: "ui-search-dropdown__empty"
}
const _hoisted_6 = {
  key: 2,
  class: "ui-search-dropdown__empty"
}
const _hoisted_7 = ["aria-selected", "onPointerdown", "onMouseenter"]
const _hoisted_8 = { class: "ui-search-dropdown__title" }
const _hoisted_9 = {
  key: 0,
  class: "ui-search-dropdown__subtitle"
}

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { onDocumentModalEvent } from '@/components/modal-events';
import { computeDropdownPosition, type DropdownPositionStyle } from '@/components/ui/dropdown-position';
import {
  filterSearchDropdownItems,
  findSearchDropdownItem,
  formatSearchDropdownValue,
  readSearchPathText,
  type SearchDropdownOutputMode,
  type SearchDropdownVariant
} from './SearchDropdown';

type SearchDropdownItem = Record<string, unknown>;


export default /*@__PURE__*/_defineComponent({
  __name: 'SearchDropdown',
  props: {
    modelValue: { default: '' },
    items: { default: () => [] },
    valuePath: { default: 'value' },
    subtitlePath: { default: 'subtitle' },
    searchKeys: { default: undefined },
    getValue: { type: Function, default: undefined },
    getSubtitle: { type: Function, default: undefined },
    getSearchTexts: { type: Function, default: undefined },
    getItemKey: { type: Function, default: undefined },
    label: { default: '' },
    placeholder: { default: '' },
    ariaLabel: { default: '' },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    minChars: { default: 2 },
    maxResults: { default: 8 },
    outputMode: { default: 'value' },
    valueSeparator: { default: ': ' },
    variant: { default: 'form' }
  },
  emits: ["update:modelValue", "select"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const rootRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);
const menuStyle = ref<DropdownPositionStyle>({});
const query = ref('');
const isOpen = ref(false);
const activeIndex = ref(-1);
const instanceId = `ui-search-dropdown-${Math.random().toString(36).slice(2, 9)}`;
const inputId = `${instanceId}-input`;
const menuId = `${instanceId}-menu`;
let removeModalHideListener: (() => void) | null = null;

const shellClasses = computed(() => [
  `ui-search-dropdown--${props.variant}`,
  {
    'is-open': isOpen.value,
    'is-disabled': props.disabled
  }
]);

const resolvedAriaLabel = computed(() => props.ariaLabel || props.label || 'Search');
const normalizedQuery = computed(() => query.value.toLowerCase().trim());

const results = computed<SearchDropdownItem[]>(() => filterSearchDropdownItems<SearchDropdownItem>({
  items: props.items,
  query: query.value,
  valuePath: props.valuePath,
  subtitlePath: props.subtitlePath,
  searchKeys: props.searchKeys,
  minChars: props.minChars,
  maxResults: props.maxResults,
  getValue: props.getValue,
  getSubtitle: props.getSubtitle,
  getSearchTexts: props.getSearchTexts
}));

function formatConfig() {
  return {
    valuePath: props.valuePath,
    subtitlePath: props.subtitlePath,
    getValue: props.getValue,
    getSubtitle: props.getSubtitle,
    valueSeparator: props.valueSeparator
  };
}

function displayValueFor(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const matchedItem = findSearchDropdownItem(props.items, trimmed, {
    valuePath: props.valuePath,
    getValue: props.getValue
  });
  if (!matchedItem) return trimmed;

  return formatSearchDropdownValue(matchedItem, props.outputMode, formatConfig());
}

function resultTitle(item: SearchDropdownItem): string {
  if (props.getValue) return props.getValue(item);
  return readSearchPathText(item, props.valuePath);
}

function resultSubtitle(item: SearchDropdownItem): string {
  if (props.getSubtitle) return props.getSubtitle(item);
  if (!props.subtitlePath) return '';
  return readSearchPathText(item, props.subtitlePath);
}

function itemKey(item: SearchDropdownItem, index: number): string {
  if (props.getItemKey) return props.getItemKey(item, index);
  return `${resultTitle(item)}-${index}`;
}

function isSelected(item: SearchDropdownItem): boolean {
  const value = props.getValue ? props.getValue(item) : readSearchPathText(item, props.valuePath);
  return value === props.modelValue;
}

function updateMenuPosition(): void {
  if (!inputRef.value || !menuRef.value) return;
  menuStyle.value = computeDropdownPosition({
    trigger: inputRef.value,
    panel: menuRef.value,
    placement: 'auto',
    offset: 4,
    matchTriggerWidth: true,
    zIndex: 2600
  });
}

function schedulePositionUpdate(): void {
  void nextTick(() => {
    updateMenuPosition();
    window.requestAnimationFrame(() => updateMenuPosition());
  });
}

function openMenu(): void {
  if (props.disabled) return;
  isOpen.value = true;
  activeIndex.value = results.value.length ? 0 : -1;
  schedulePositionUpdate();
}

function closeMenu(): void {
  isOpen.value = false;
  activeIndex.value = -1;
  menuStyle.value = {};
}

function handleInput(event: Event): void {
  const nextValue = (event.target as HTMLInputElement).value;
  query.value = nextValue;
  emit('update:modelValue', nextValue);
  isOpen.value = true;
  activeIndex.value = results.value.length ? 0 : -1;
  schedulePositionUpdate();
}

function moveSelection(direction: 1 | -1): void {
  if (!isOpen.value) {
    openMenu();
    return;
  }
  if (!results.value.length) return;
  activeIndex.value = (activeIndex.value + direction + results.value.length) % results.value.length;
}

function commitActiveSelection(): void {
  const item = results.value[activeIndex.value];
  if (item) selectItem(item);
}

function selectItem(item: SearchDropdownItem): void {
  const nextValue = formatSearchDropdownValue(item, props.outputMode, formatConfig());
  query.value = nextValue;
  emit('update:modelValue', nextValue);
  emit('select', item);
  closeMenu();
}

function handleDocumentPointerDown(event: PointerEvent): void {
  const target = event.target as Node | null;
  if (!target) return;
  if (rootRef.value?.contains(target) || menuRef.value?.contains(target)) return;
  closeMenu();
}

function handleViewportChange(): void {
  if (isOpen.value) updateMenuPosition();
}

function focus(): void {
  inputRef.value?.focus();
}

watch(() => props.modelValue, value => {
  const nextDisplay = displayValueFor(value);
  if (nextDisplay !== query.value) query.value = nextDisplay;
}, { immediate: true });

watch(results, nextResults => {
  if (!nextResults.length) activeIndex.value = -1;
  else if (activeIndex.value < 0 || activeIndex.value >= nextResults.length) activeIndex.value = 0;
  if (isOpen.value) schedulePositionUpdate();
});

watch(() => props.disabled, disabled => {
  if (disabled) closeMenu();
});

watch(isOpen, open => {
  if (open) {
    window.addEventListener('resize', handleViewportChange, true);
    window.addEventListener('scroll', handleViewportChange, true);
    return;
  }
  window.removeEventListener('resize', handleViewportChange, true);
  window.removeEventListener('scroll', handleViewportChange, true);
});

onMounted(() => {
  document.addEventListener('pointerdown', handleDocumentPointerDown);
  removeModalHideListener = onDocumentModalEvent('hide.bs.modal', closeMenu);
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleDocumentPointerDown);
  removeModalHideListener?.();
  window.removeEventListener('resize', handleViewportChange, true);
  window.removeEventListener('scroll', handleViewportChange, true);
});

__expose({
  focus,
  closeMenu,
  inputRef
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "rootRef",
    ref: rootRef,
    class: _normalizeClass(["ui-search-dropdown", shellClasses.value])
  }, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          for: inputId,
          class: "ui-search-dropdown__label"
        }, [
          (__props.required)
            ? (_openBlock(), _createElementBlock("span", _hoisted_1, "*"))
            : _createCommentVNode("v-if", true),
          _createTextVNode(_toDisplayString(__props.label), 1 /* TEXT */)
        ]))
      : _createCommentVNode("v-if", true),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("input", {
        id: inputId,
        ref_key: "inputRef",
        ref: inputRef,
        value: query.value,
        type: "text",
        class: _normalizeClass(["ui-input ui-search-dropdown__input", [`ui-input--${__props.variant}`]]),
        placeholder: __props.placeholder,
        disabled: __props.disabled,
        required: __props.required,
        "aria-label": resolvedAriaLabel.value,
        "aria-expanded": isOpen.value ? 'true' : 'false',
        "aria-autocomplete": "list",
        "aria-controls": menuId,
        role: "combobox",
        autocomplete: "off",
        onFocus: openMenu,
        onInput: handleInput,
        onKeydown: [
          _cache[0] || (_cache[0] = _withKeys(_withModifiers(($event: any) => (moveSelection(1)), ["prevent"]), ["down"])),
          _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (moveSelection(-1)), ["prevent"]), ["up"])),
          _withKeys(_withModifiers(commitActiveSelection, ["prevent"]), ["enter"]),
          _withKeys(_withModifiers(closeMenu, ["prevent"]), ["esc"])
        ]
      }, null, 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_3),
      (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
        (isOpen.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              id: menuId,
              ref_key: "menuRef",
              ref: menuRef,
              class: "ui-search-dropdown__menu ui-search-dropdown__menu--portal",
              style: _normalizeStyle(menuStyle.value),
              role: "listbox"
            }, [
              (__props.loading)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, "Loading results..."))
                : (__props.minChars > 0 && normalizedQuery.value.length < __props.minChars)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, " Type at least " + _toDisplayString(__props.minChars) + " characters. ", 1 /* TEXT */))
                  : (results.value.length === 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, "No matching results found."))
                    : (_openBlock(true), _createElementBlock(_Fragment, { key: 3 }, _renderList(results.value, (item, index) => {
                        return (_openBlock(), _createElementBlock("button", {
                          key: itemKey(item, index),
                          type: "button",
                          role: "option",
                          class: _normalizeClass(["ui-search-dropdown__option", { 'is-active': activeIndex.value === index }]),
                          "aria-selected": isSelected(item) ? 'true' : 'false',
                          onPointerdown: _withModifiers(($event: any) => (selectItem(item)), ["prevent"]),
                          onMouseenter: ($event: any) => (activeIndex.value = index)
                        }, [
                          _createElementVNode("span", _hoisted_8, _toDisplayString(resultTitle(item)), 1 /* TEXT */),
                          (resultSubtitle(item))
                            ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(resultSubtitle(item)), 1 /* TEXT */))
                            : _createCommentVNode("v-if", true)
                        ], 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_7))
                      }), 128 /* KEYED_FRAGMENT */))
            ], 4 /* STYLE */))
          : _createCommentVNode("v-if", true)
      ]))
    ])
  ], 2 /* CLASS */))
}
}

})